import React from "react";

const EnglishQuizQuestion = ({
  question,
  number,
  selectedAnswer,
  onAnswerChange,
}) => {
  if (!question) {
    return null;
  }

  const {
    questionText,
    questionType,
    options = [],
    highlightedText,
    statements = [],
    imageUrl,
  } = question;

  // ======================================================
  // Single Choice
  // ======================================================

  const renderSingleChoice = () => (
    <div className="mt-4 space-y-3">
      {options.map((option) => (
        <label
          key={option.key}
          className="
            flex
            items-center
            gap-3
            rounded-xl
            border
            border-gray-200
            p-3
            cursor-pointer
            hover:bg-gray-50
          "
        >
          <input
            type="radio"
            name={`question-${question._id}`}
            value={option.key}
            checked={selectedAnswer === option.key}
            onChange={() =>
              onAnswerChange(
                question._id,
                option.key,
              )
            }
          />

          <span className="font-medium text-gray-700">
            {option.key}. {option.text}
          </span>
        </label>
      ))}
    </div>
  );

  // ======================================================
  // Multiple Choice
  // ======================================================

  const renderMultipleChoice = () => {
    const selected =
      Array.isArray(selectedAnswer)
        ? selectedAnswer
        : [];

    const handleChange = (key) => {
      let updated = [];

      if (selected.includes(key)) {
        updated = selected.filter(
          (item) => item !== key,
        );
      } else {
        updated = [...selected, key];
      }

      onAnswerChange(
        question._id,
        updated,
      );
    };

    return (
      <div className="mt-4 space-y-3">
        {options.map((option) => (
          <label
            key={option.key}
            className="
              flex
              items-center
              gap-3
              rounded-xl
              border
              border-gray-200
              p-3
              cursor-pointer
              hover:bg-gray-50
            "
          >
            <input
              type="checkbox"
              value={option.key}
              checked={selected.includes(
                option.key,
              )}
              onChange={() =>
                handleChange(option.key)
              }
            />

            <span className="font-medium text-gray-700">
              {option.key}. {option.text}
            </span>
          </label>
        ))}
      </div>
    );
  };

  // ======================================================
  // Fill In The Blank
  // ======================================================

  const renderFillInTheBlank = () => (
    <div className="mt-4">
      <input
        type="text"
        value={selectedAnswer || ""}
        onChange={(event) =>
          onAnswerChange(
            question._id,
            event.target.value,
          )
        }
        className="
          w-full
          rounded-xl
          border
          border-gray-300
          px-4
          py-3
          outline-none
          focus:border-blue-500
        "
        placeholder="Enter your answer"
      />
    </div>
  );

  // ======================================================
  // Question Type
  // ======================================================

  const renderAnswerInput = () => {
    switch (questionType) {
      case "multipleChoice":
        return renderMultipleChoice();

      case "fillInTheBlank":
        return renderFillInTheBlank();

      case "singleChoice":
      default:
        return renderSingleChoice();
    }
  };

  return (
    <div
      className="
        rounded-2xl
        border
        border-gray-200
        bg-white
        p-5
      "
    >
      {/* Question Number */}

      <div className="flex items-start gap-3">

        <span
          className="
            flex
            h-8
            w-8
            shrink-0
            items-center
            justify-center
            rounded-full
            bg-blue-100
            text-sm
            font-bold
            text-blue-700
          "
        >
          {number}
        </span>

        <div className="flex-1">

          {/* Question Text */}

          <p className="text-lg font-semibold text-gray-800 whitespace-pre-line">
            {questionText}
          </p>

          {/* Highlighted Text */}

          {highlightedText && (
            <div className="mt-3 rounded-lg bg-yellow-50 p-3">
              <p className="font-semibold text-gray-800">
                {highlightedText}
              </p>
            </div>
          )}

          {/* Statements */}

          {statements.length > 0 && (
            <div className="mt-4 space-y-2">
              {statements.map((statement) => (
                <div
                  key={statement.key}
                  className="rounded-lg bg-gray-50 p-3"
                >
                  <span className="font-semibold">
                    {statement.key}.
                  </span>{" "}
                  {statement.text}
                </div>
              ))}
            </div>
          )}

          {/* Question Image */}

          {imageUrl && (
            <div className="mt-4">
              <img
                src={imageUrl}
                alt="Question"
                className="max-w-full rounded-xl"
              />
            </div>
          )}

          {/* Answer */}

          {renderAnswerInput()}

        </div>
      </div>
    </div>
  );
};

export default EnglishQuizQuestion;